import { Camera, KeyRound, Route, ShieldCheck, Dog, Fence, Siren, ClipboardCheck, ArrowUpRight } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";

const services = [
  {
    id: "cctv", icon: Camera, span: "lg:col-span-2",
    title: "CCTV & Thermal Optics",
    desc: "Panomera-grade camera matrices, thermal imaging and 24/7 monitored feeds with AI motion flagging across every blind spot.",
    metric: "360° Coverage", status: "Active",
  },
  {
    id: "fob", icon: KeyRound, span: "",
    title: "Key Fob & Biometric Entry",
    desc: "Encrypted key fob access control, turnstile integration and audit-logged entry for every door that matters.",
    metric: "AES-256", status: "Online",
  },
  {
    id: "patrols", icon: Route, span: "",
    title: "Mobile Guard Patrols",
    desc: "GPS-verified patrol routes with randomized timing windows and live checkpoint reporting to the command desk.",
    metric: "24/7 Rounds", status: "Deployed",
  },
  {
    id: "static", icon: ShieldCheck, span: "",
    title: "Static Guard Placement",
    desc: "Licensed officers anchored at gates, lobbies and high-value chokepoints. A fixed line that never blinks.",
    metric: "Fixed Post", status: "On Site",
  },
  {
    id: "k9", icon: Dog, span: "",
    title: "Dual-Purpose K9 Units",
    desc: "Certified patrol and detection dogs with dedicated handlers. Visible deterrence, instant pursuit capability.",
    metric: "Level III", status: "K9 Ready",
  },
  {
    id: "compound", icon: Fence, span: "lg:col-span-2",
    title: "Compound K9 Patrols",
    desc: "Free-running perimeter dogs securing fenced compounds, yards and logistics parks through the night — the deterrent intruders fear most.",
    metric: "Perimeter", status: "Night Ops",
  },
  {
    id: "alarm", icon: Siren, span: "",
    title: "Rapid Alarm Response",
    desc: "Alarm activation to boots-on-ground in under 15 minutes, with full incident reporting and evidence capture.",
    metric: "<15 Min", status: "Standby",
  },
  {
    id: "consulting", icon: ClipboardCheck, span: "",
    title: "Risk Assessment & Consulting",
    desc: "Full-site vulnerability audits, threat modeling and executive security consulting before breaches happen.",
    metric: "Audit", status: "By Appt",
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          testid="services-heading"
          overline="Capabilities / 08"
          title="One Integrated Defense Matrix"
          description="Every capability below operates standalone or fused into a single command structure — hardware, humans and hounds working the same clock."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {services.map((s, i) => (
            <Reveal key={s.id} delay={(i % 4) * 0.08} className={s.span}>
              <article
                data-testid={`service-card-${s.id}`}
                className="group relative h-full clip-corners bg-[#0F1115] border border-white/10 hover:border-blue-500/40 p-7 flex flex-col gap-5 transition-colors duration-500 overflow-hidden"
              >
                <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-blue-600/0 group-hover:bg-blue-600/10 blur-3xl transition-all duration-700" />
                <div className="flex items-start justify-between">
                  <span className="clip-corners bg-[#161920] border border-white/10 group-hover:border-blue-500/40 p-3 transition-colors duration-500">
                    <s.icon className="w-6 h-6 text-blue-500" strokeWidth={1.75} />
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-emerald-400 pulse-dot" /> {s.status}
                  </span>
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-xl font-bold tracking-tight text-gray-100 mb-2 flex items-center gap-2">
                    {s.title}
                    <ArrowUpRight className="w-4 h-4 text-gray-600 group-hover:text-blue-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{s.desc}</p>
                </div>
                <div className="flex items-center justify-between border-t border-white/5 pt-4">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500">{s.metric}</span>
                  <span className="font-mono text-xs text-blue-500/80">0{i + 1}</span>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
